import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CodeMirror from './common/CodeMirrorWithWrap'
import { markdown } from '@codemirror/lang-markdown'
import { oneDark } from '@codemirror/theme-one-dark'
import { EditorView } from '@codemirror/view'
import { api } from '../api'
import TokenBadge from './common/TokenBadge'
import './InstructionEditor.css'

/**
 * 인스트럭션 편집기 — page-style.
 *
 * 좌측: 편집 가능한 인스트럭션 이름 목록 (디스크 defaults 기준)
 * 우측: CodeMirror 마크다운 에디터 + 헤더(이름 / dirty 배지) + 액션 버튼들
 *
 * 모드:
 *   user    — 본인 override 만 편집/저장 (대다수 사용자)
 *   default — admin 만 노출. 공유 기본값(_default 격) 편집/저장
 */

// ── 인스트럭션별 설명 + 짧은 표시명 ────────────────────────────────────────
const INSTRUCTION_META = {
  metric_view_system: {
    label: 'Metric View 시스템',
    desc:  'Metric View YAML 생성 시 LLM에 전달하는 시스템 프롬프트',
  },
  metric_view_user_prompt: {
    label: 'Metric View 사용자 프롬프트',
    desc:  'Metric View 생성 요청 시 메타데이터를 채워 LLM에 보내는 사용자 프롬프트 템플릿',
  },
  // ── Workbench 변환 4종 (a→b 케이스별) — KBNL-128: 방향 표기로 매칭 명확화 ──
  sql_to_semantic_system: {
    label: 'Workbench SQL→YAML (생성) 시스템',
    desc:  'Workbench 의 YAML 생성(SQL→YAML) step — SQL 쿼리를 Semantic/Composite View YAML 로 변환할 때 사용하는 시스템 프롬프트',
  },
  sql_to_semantic_user_prompt: {
    label: 'Workbench SQL→YAML (생성) 사용자 프롬프트',
    desc:  'YAML 생성 요청 시 SQL·스키마·용어집을 채워 LLM에 보내는 사용자 프롬프트 템플릿',
  },
  sql_workbench_refine_system: {
    label: 'Workbench SQL→SQL (정제) 시스템',
    desc:  'Workbench 의 SQL 정제(SQL→SQL) step 에서 LLM 에 전달하는 시스템 프롬프트 — 단계별 SQL 변형/리팩토링 규칙',
  },
  sql_workbench_refine_user_prompt: {
    label: 'Workbench SQL→SQL (정제) 사용자 프롬프트',
    desc:  'SQL 정제 step 에서 LLM 에 보내는 사용자 프롬프트 템플릿 — {base_sql}, {user_prompt} placeholder 에 직전 step SQL 과 사용자 지시가 주입',
  },
  sql_workbench_refine_yaml_system: {
    label: 'Workbench YAML→YAML (정제) 시스템',
    desc:  'Workbench 의 YAML 정제(YAML→YAML) step 에서 LLM 에 전달하는 시스템 프롬프트 — Semantic/Composite View YAML 의 변형/품질 보강 규칙',
  },
  sql_workbench_refine_yaml_user_prompt: {
    label: 'Workbench YAML→YAML (정제) 사용자 프롬프트',
    desc:  'YAML 정제 step 에서 LLM 에 보내는 사용자 프롬프트 템플릿 — {base_yaml}, {user_prompt} placeholder 에 직전 step YAML 과 사용자 지시가 주입',
  },
  yaml_to_sql_system: {
    label: 'Workbench YAML→SQL (LLM 엔진) 시스템',
    desc:  'Workbench 의 YAML→SQL step 에서 엔진을 LLM 으로 선택했을 때의 시스템 프롬프트 — 환각 금지(YAML 정의 외 추가 금지) 원칙 포함. 규칙 엔진(기본)은 LLM/인스트럭션 미사용',
  },
  yaml_to_sql_user_prompt: {
    label: 'Workbench YAML→SQL (LLM 엔진) 사용자 프롬프트',
    desc:  'YAML→SQL(LLM) 실행 시 LLM 에 보내는 사용자 프롬프트 템플릿 — {base_yaml}, {user_prompt}, {glossary} placeholder 사용',
  },
  summarize_meta_system: {
    label: '메타 요약 시스템',
    desc:  '테이블 메타 정보를 요약할 때 사용하는 시스템 프롬프트',
  },
  summarize_table_schema_system: {
    label: '스키마 요약 시스템',
    desc:  'Virtual Schema 의 "요약해서 적용" 시 LLM 에 전달하는 시스템 프롬프트 — 역할/출력 원칙 (YAML 형식 + 고유 식별자 보존)',
  },
  summarize_table_schema_user_prompt: {
    label: '스키마 요약 사용자 프롬프트',
    desc:  'Virtual Schema 의 "요약해서 적용" 시 LLM 에 보내는 사용자 프롬프트 템플릿 — {original} placeholder 에 원본 텍스트 주입',
  },
  sandbox_system: {
    label: 'Sandbox 시스템',
    desc:  'Sandbox(자유 LLM 작업 탭) 의 시스템 프롬프트. 사용자 설정의 메타(테이블 스키마, 용어집)를 LLM 에 어떻게 주입할지 정의 — {table_metadata}, {glossary} placeholder 사용',
  },
  composite_combine_system: {
    label: 'Composite 조합 시스템',
    desc:  'Composite View 의 [AI 조합] 모드에서 LLM 에 전달하는 시스템 프롬프트 — 여러 semantic_view / composite_view 를 사용자 의도에 맞게 통합 YAML 로 만드는 규칙',
  },
  composite_combine_user_prompt: {
    label: 'Composite 조합 사용자 프롬프트',
    desc:  'Composite View [AI 조합] 실행 시 LLM 에 보내는 사용자 프롬프트 템플릿 — {view_blocks} 에 입력 뷰 YAML 들이, {user_prompt} 에 사용자 결합 의도가 채워짐',
  },
  composite_combine_default_prompt: {
    label: 'Composite 조합 기본 의도',
    desc:  'Composite View [AI 조합] 에서 사용자가 프롬프트를 비우고 실행했을 때 적용되는 기본 결합 의도. 비워두면 이 문구로 조합됩니다.',
  },
}

const cmBaseTheme = EditorView.theme({
  '&':            { fontSize: 'var(--code-font-size, 13px)' },
  '.cm-content':  { fontFamily: 'var(--font-code)', padding: '12px 0' },
  '.cm-gutters':  { fontFamily: 'var(--font-code)' },
  '.cm-line':     { padding: '0 12px' },
  '.cm-scroller': { overflow: 'auto' },
})

export default function InstructionEditor({ settings, isAdmin, onClose, showToast, editorTheme }) {
  const [mode, setMode] = useState('user')
  const [items, setItems] = useState([])
  const [loadingList, setLoadingList] = useState(false)
  // KBNL-128 후속: 목록을 화면 표시 라벨 기준 알파벳/가나다순 정렬 (사용자 결정).
  // 기존엔 영문 파일명순이라 한글 라벨 화면에선 순서가 뒤섞여 보였음.
  // 라벨 정렬 덕에 'Workbench …' 4쌍 등 같은 접두 그룹이 자연히 모임.
  const sortedItems = useMemo(() => [...items].sort((a, b) =>
    (INSTRUCTION_META[a.name]?.label || a.name)
      .localeCompare(INSTRUCTION_META[b.name]?.label || b.name, 'ko')
  ), [items])

  const [selectedName, setSelectedName] = useState(null)
  const [loaded, setLoaded] = useState(null)
  const [editing, setEditing] = useState('')
  const [loadingItem, setLoadingItem] = useState(false)
  const [saving, setSaving] = useState(false)
  // KBNL-136: 수정 이력(버전 관리) 패널 상태
  const [historyOpen, setHistoryOpen] = useState(false)
  const [historyEntries, setHistoryEntries] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  // KBNL-141: 이력 내용 보기 — 선택된 버전의 전체 내용을 우측 패널에 표시 (master-detail)
  const [selectedHistId, setSelectedHistId] = useState(null)
  const [historyContent, setHistoryContent] = useState('')
  const [historyContentLoading, setHistoryContentLoading] = useState(false)
  // KBNL-141: 헤더 요약 — 선택한 인스트럭션의 이력 개수 + 최종 수정 시각. { count, latestAt } | null
  const [historySummary, setHistorySummary] = useState(null)

  const dirty = useMemo(
    () => loaded != null && editing !== (loaded.content || ''),
    [editing, loaded]
  )

  // ── 목록 로드 ───────────────────────────────────────────────────────────
  // KBNL-131: '기본값 변경됨' 토스트는 페이지 진입 후 1회만
  const defaultChangeToastRef = useRef(false)

  const loadList = useCallback(async () => {
    setLoadingList(true)
    try {
      const res = await api.listInstructions()
      setItems(res.items || [])
      // KBNL-131(B): 공유 기본값이 변경된 항목 안내 (1회)
      const changed = (res.items || []).filter(i => i.default_changed)
      if (changed.length > 0 && !defaultChangeToastRef.current) {
        defaultChangeToastRef.current = true
        showToast?.('warning', `공유 기본값이 변경된 인스트럭션 ${changed.length}건 — 목록의 ⚠ 항목을 확인하세요.`)
      }
      if (!selectedName && (res.items || []).length > 0) {
        setSelectedName(res.items[0].name)
      }
    } catch (e) {
      showToast?.('error', `인스트럭션 목록 로드 실패: ${e.message}`)
    } finally {
      setLoadingList(false)
    }
  }, [selectedName, showToast])

  // Why setTimeout 위임: 이펙트 동기 경로에서 setState 를 제거 (react-hooks/set-state-in-effect).
  // 룰이 api 호출의 동기 throw 가능성까지 보수적으로 추적하므로 콜백으로 한 틱 미룸.
  // cleanup 으로 unmount/의존성 변경 시 호출 취소 — StrictMode 이중 실행에도 안전.
  useEffect(() => { const t = setTimeout(loadList, 0); return () => clearTimeout(t) }, [loadList])

  // ── 단건 로드 ───────────────────────────────────────────────────────────
  const loadItem = useCallback(async (name, m = mode) => {
    if (!name) { setLoaded(null); setEditing(''); return }
    setLoadingItem(true)
    try {
      const res = m === 'default'
        ? await api.getAdminInstruction(name)
        : await api.getInstruction(name)
      setLoaded(res)
      setEditing(res.content || '')
    } catch (e) {
      showToast?.('error', `로드 실패: ${e.message}`)
      setLoaded(null)
      setEditing('')
    } finally {
      setLoadingItem(false)
    }
  }, [mode, showToast])

  // KBNL-141: 선택 인스트럭션의 이력 요약(개수 + 최종 수정 시각) 조회 — 헤더 표시용.
  // 패널 열 때 쓰던 이력 API 를 선택 시점에 1회 호출(가벼움). user/default 스코프 분기.
  const fetchHistorySummary = useCallback(async (name, m = mode) => {
    if (!name) { setHistorySummary(null); return }
    try {
      const res = m === 'default'
        ? await api.getAdminInstructionHistory(name)
        : await api.getInstructionHistory(name)
      const entries = res.entries || []
      setHistorySummary({ count: entries.length, latestAt: entries[0]?.updated_at || null })
    } catch {
      setHistorySummary(null)
    }
  }, [mode])

  // Why setTimeout 위임: loadList 의 이펙트와 동일 — set-state-in-effect 회피 + 취소 가능.
  useEffect(() => {
    const t = setTimeout(() => { loadItem(selectedName, mode); fetchHistorySummary(selectedName, mode) }, 0)
    return () => clearTimeout(t)
  }, [selectedName, mode, loadItem, fetchHistorySummary])

  // ── 액션 ────────────────────────────────────────────────────────────────
  const confirmDiscardIfDirty = () => {
    if (!dirty) return true
    return confirm('저장하지 않은 변경이 있습니다. 버릴까요?')
  }

  const handleSelect = (name) => {
    if (name === selectedName) return
    if (!confirmDiscardIfDirty()) return
    setSelectedName(name)
  }

  const handleSwitchMode = (next) => {
    if (next === mode) return
    if (!confirmDiscardIfDirty()) return
    setMode(next)
  }

  const handleLoadDefault = async () => {
    if (!selectedName) return
    // KBNL-136: '기본값' 의 의미를 모드별로 구분.
    //   user 모드(일반 사용자): 운영 기본값 = 관리자가 저장한 공유 기본값(있으면).
    //     없을 때만 디스크 공장값으로 폴백. (기존엔 항상 디스크 공장값을 로드해
    //     "관리자 저장값이 아닌 공장초기화값이 나온다"는 피드백 발생)
    //   default 모드(SVS admin): 공유 기본값을 공장값으로 되돌리는 용도라 디스크 그대로.
    const wantsFactory = mode === 'default'
    const label = wantsFactory ? '디스크 공장 기본값' : '공유 기본값(관리자 저장값)'
    if (dirty && !confirm(`현재 편집 내용을 ${label}으로 덮어씁니다. 진행할까요?`)) return
    try {
      const res = wantsFactory
        ? await api.getInstructionDefault(selectedName)   // 디스크 defaults/*.md (공장값)
        : await api.getInstructionShared(selectedName)     // 공유 기본값 → 없으면 디스크 폴백
      setEditing(res.content || '')
      const srcLabel = res.source === 'disk' ? '디스크 공장 기본값' : '공유 기본값(관리자 저장값)'
      showToast?.('success', `${srcLabel}을 에디터에 로드했습니다. 저장 버튼으로 반영하세요.`)
    } catch (e) {
      showToast?.('error', `기본값 로드 실패: ${e.message}`)
    }
  }

  const handleSave = async () => {
    if (!selectedName) return
    setSaving(true)
    try {
      if (mode === 'default') {
        await api.saveAdminInstruction(selectedName, editing)
        showToast?.('success', `공유 기본값 저장됨: ${selectedName}`)
      } else {
        await api.saveInstruction(selectedName, editing)
        showToast?.('success', `내 인스트럭션 저장됨: ${selectedName}`)
      }
      await loadItem(selectedName, mode)
      fetchHistorySummary(selectedName, mode)   // 저장 시 이력 1건 추가 → 요약 갱신
      loadList()
    } catch (e) {
      showToast?.('error', `저장 실패: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  // ── KBNL-131: 기본값 변경 알림 액션 ──────────────────────────────────
  const handleLoadShared = async () => {
    if (!selectedName) return
    if (dirty && !confirm('현재 편집 내용을 공유 기본값으로 덮어씁니다. 진행할까요?')) return
    try {
      const res = await api.getInstructionShared(selectedName)
      setEditing(res.content || '')
      showToast?.('success', '변경된 공유 기본값을 에디터에 로드했습니다. (저장 전까지 내 설정엔 반영되지 않음)')
    } catch (e) {
      showToast?.('error', `공유 기본값 로드 실패: ${e.message}`)
    }
  }

  const handleAckDefaultChange = async () => {
    if (!selectedName) return
    try {
      await api.ackInstructionDefaultChange(selectedName)
      showToast?.('success', '내 설정을 유지합니다 — 이 변경 알림을 껐습니다.')
      loadList()
    } catch (e) {
      showToast?.('error', `처리 실패: ${e.message}`)
    }
  }

  const handleDeleteOverride = async () => {
    if (!selectedName || mode !== 'user') return
    if (!loaded?.has_user_override) return
    if (!confirm('내 override 를 삭제하고 공유 기본값으로 되돌릴까요?')) return
    try {
      await api.deleteInstruction(selectedName)
      showToast?.('success', `override 삭제: ${selectedName}`)
      await loadItem(selectedName, mode)
      fetchHistorySummary(selectedName, mode)
      loadList()
    } catch (e) {
      showToast?.('error', `삭제 실패: ${e.message}`)
    }
  }

  // ── KBNL-136: 수정 이력(버전 관리) ──────────────────────────────────────

  // KBNL-141: 타임스탬프 → 'yyyy-MM-dd HH:mm:ss' (로컬 시간). 파싱 실패 시 원문.
  const fmtTimestamp = (v) => {
    if (!v) return '-'
    const d = new Date(v)
    if (isNaN(d.getTime())) return String(v)
    const p = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} `
      + `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
  }

  const openHistory = async () => {
    if (!selectedName) return
    setHistoryOpen(true)
    setHistoryLoading(true)
    setSelectedHistId(null)
    setHistoryContent('')
    try {
      // user 모드: 내 override 이력 / default 모드(admin): 공유 기본값 이력
      const res = mode === 'default'
        ? await api.getAdminInstructionHistory(selectedName)
        : await api.getInstructionHistory(selectedName)
      const entries = res.entries || []
      setHistoryEntries(entries)
      // 최신(첫) 버전을 자동 선택해 바로 내용을 보여준다.
      if (entries.length) selectHistoryEntry(entries[0].id)
    } catch (e) {
      showToast?.('error', `이력 로드 실패: ${e.message}`)
      setHistoryEntries([])
    } finally {
      setHistoryLoading(false)
    }
  }

  // KBNL-141: 목록에서 버전 클릭 → 전체 내용을 우측 패널에 로드 (읽기 전용).
  const selectHistoryEntry = async (entryId) => {
    if (!entryId) return
    setSelectedHistId(entryId)
    setHistoryContentLoading(true)
    try {
      const res = mode === 'default'
        ? await api.getAdminInstructionHistoryEntry(selectedName, entryId)
        : await api.getInstructionHistoryEntry(selectedName, entryId)
      setHistoryContent(res.content || '')
    } catch (e) {
      setHistoryContent('')
      showToast?.('error', `버전 내용 로드 실패: ${e.message}`)
    } finally {
      setHistoryContentLoading(false)
    }
  }

  // KBNL-141: 이력 단건 삭제 — 확인 후 삭제, 목록/선택 보정.
  const handleDeleteHistoryEntry = async (entryId) => {
    if (!selectedName || !entryId) return
    if (!confirm('이 버전을 이력에서 삭제합니다. 되돌릴 수 없습니다. 진행할까요?')) return
    try {
      if (mode === 'default') {
        await api.deleteAdminInstructionHistoryEntry(selectedName, entryId)
      } else {
        await api.deleteInstructionHistoryEntry(selectedName, entryId)
      }
      const remaining = historyEntries.filter(e => e.id !== entryId)
      setHistoryEntries(remaining)
      // 헤더 요약 갱신 (개수 -1, 최종 시각 재계산)
      setHistorySummary({ count: remaining.length, latestAt: remaining[0]?.updated_at || null })
      if (selectedHistId === entryId) {
        if (remaining.length) {
          selectHistoryEntry(remaining[0].id)
        } else {
          setSelectedHistId(null)
          setHistoryContent('')
        }
      }
      showToast?.('success', '이력 버전을 삭제했습니다.')
    } catch (e) {
      showToast?.('error', `이력 삭제 실패: ${e.message}`)
    }
  }

  const handleLoadHistoryEntry = async (entryId) => {
    if (!selectedName || !entryId) return
    if (dirty && !confirm('현재 편집 내용을 이 버전으로 덮어씁니다. 진행할까요?')) return
    try {
      const res = mode === 'default'
        ? await api.getAdminInstructionHistoryEntry(selectedName, entryId)
        : await api.getInstructionHistoryEntry(selectedName, entryId)
      setEditing(res.content || '')
      setHistoryOpen(false)
      showToast?.('success', '선택한 버전을 에디터에 불러왔습니다. 저장하면 새 버전으로 기록됩니다.')
    } catch (e) {
      showToast?.('error', `버전 로드 실패: ${e.message}`)
    }
  }

  // ── 렌더 ────────────────────────────────────────────────────────────────
  const cmExtensions = useMemo(() => [markdown(), cmBaseTheme], [])

  return (
    <div className="instr-editor">
      {/* 헤더 */}
      <div className="instr-head">
        <span className="instr-head-title">
          인스트럭션 편집
          {/* KBNL-129 후속: 모드 뱃지 제거 (사용자 결정) — 텍스트 길이 차로 토글이
              흔들렸고, 현재 모드는 토글 active 하이라이트가 이미 표시. */}
        </span>
        {isAdmin && (
          <div className="instr-mode-toggle">
            <button
              className={`mode-btn ${mode === 'user' ? 'active' : ''}`}
              onClick={() => handleSwitchMode('user')}
            >내 설정</button>
            <button
              className={`mode-btn ${mode === 'default' ? 'active' : ''}`}
              onClick={() => handleSwitchMode('default')}
            >공유 기본값</button>
          </div>
        )}
        <span style={{ flex: 1 }} />
        <button className="btn-icon" onClick={onClose} title="닫기">✕</button>
      </div>

      {/* 본문 */}
      <div className="instr-body">
        {/* 좌측 목록 — std-list 패턴 (다른 사이드바와 동일 톤). 인스트럭션
            편집기 고유 정보(파일명 / dirty / override 배지) 는 name-row 의
            우측 슬롯과 meta 라인에 inline 으로 배치. */}
        <aside className="instr-list std-list">
          {loadingList && items.length === 0 && (
            <div className="std-list-empty">로드 중…</div>
          )}
          {!loadingList && items.length === 0 && (
            <div className="std-list-empty">인스트럭션이 없습니다.</div>
          )}
          {sortedItems.map(it => {
            const isSel = it.name === selectedName
            const isDirty = isSel && dirty
            const meta = INSTRUCTION_META[it.name]
            return (
              <div
                key={it.name}
                className={`std-list-item ${isSel ? 'active' : ''} ${it.has_user_override ? 'has-override' : ''}`}
                onClick={() => handleSelect(it.name)}
                title={meta?.desc || it.name}
              >
                <div className="std-list-name-row">
                  <span className="std-list-name">{meta?.label || it.name}</span>
                  {isDirty && <span className="instr-list-dirty" title="수정됨">●</span>}
                  {!isDirty && it.has_user_override && (
                    <span className="instr-list-badge" title="내 override">U</span>
                  )}
                  {/* KBNL-131: override 이후 admin 이 공유 기본값을 변경함 */}
                  {it.default_changed && (
                    <span className="instr-list-badge warn" title="공유 기본값이 변경되었습니다 — 항목을 열어 확인하세요">⚠</span>
                  )}
                </div>
                <div className="std-list-meta">
                  <span className="instr-list-filename">{it.name}</span>
                  <TokenBadge count={it.token_count} />
                </div>
              </div>
            )
          })}
        </aside>

        {/* 우측 에디터 */}
        <main className="instr-editor-pane">
          {!selectedName ? (
            <div className="instr-empty">왼쪽에서 인스트럭션을 선택하세요.</div>
          ) : (
            <>
              {/* KBNL-131: 내 override 이후 admin 이 공유 기본값을 변경한 경우 안내 배너 */}
              {mode === 'user' && (() => {
                const cur = items.find(i => i.name === selectedName)
                if (!cur?.default_changed) return null
                return (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                    padding: '8px 12px', margin: '0 0 6px', fontSize: 12.5, fontWeight: 600,
                    color: '#7c2d12',
                    background: 'linear-gradient(90deg, rgba(245,158,11,0.25), rgba(245,158,11,0.12))',
                    border: '1.5px solid #f59e0b', borderLeft: '6px solid #f59e0b', borderRadius: 6,
                  }}>
                    <span style={{ flex: 1 }}>
                      ⚠️ 이 인스트럭션의 <strong>공유 기본값이 변경</strong>되었습니다
                      {cur.default_updated_by ? ` — ${cur.default_updated_by}` : ''}
                      {/* KBNL-131 리뷰 fix: UTC slice → 로컬(KST) 시간으로 표시 */}
                      {cur.default_updated_at ? ` (${new Date(cur.default_updated_at).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' })})` : ''}.
                      현재는 내 설정(override)이 계속 적용됩니다.
                    </span>
                    <button className="btn-xs" onClick={handleLoadShared} style={{ fontSize: 11 }}
                      title="변경된 공유 기본값을 에디터에 불러와 비교 (저장 전까지 미반영)">기본값 불러와 비교</button>
                    <button className="btn-xs" onClick={handleAckDefaultChange} style={{ fontSize: 11 }}
                      title="내 설정을 유지하고 이 알림을 끕니다">내 설정 유지</button>
                    <button className="btn-xs" onClick={handleDeleteOverride} style={{ fontSize: 11 }}
                      title="내 override 를 삭제하고 변경된 공유 기본값을 따릅니다">기본값 따르기</button>
                  </div>
                )
              })()}
              <div className="instr-editor-head">
                <div className="instr-editor-head-left">
                  <div className="instr-editor-head-row">
                    <span className="instr-editor-name">
                      {INSTRUCTION_META[selectedName]?.label || selectedName}
                    </span>
                    {loaded && (
                      <span className={`instr-source instr-source-${loaded.source}`}>
                        {loaded.source === 'user_override' ? '내 override'
                          : loaded.source === 'shared_default' ? '공유 기본값'
                          : loaded.source === 'disk' ? '디스크 기본값'
                          : '없음'}
                      </span>
                    )}
                    {dirty && <span className="instr-dirty-badge">● 수정됨</span>}
                    {/* KBNL-69: 본문 헤더 토큰 배지 — 마지막 저장 기준 */}
                    {loaded && (
                      <TokenBadge
                        count={loaded.token_count}
                        variant="strong"
                        title="이 인스트럭션의 저장된 토큰 수 — 편집 후 저장 시 갱신"
                      />
                    )}
                    {/* KBNL-141: 수정 이력 요약 칩 (개수 · 최종 수정 시각) */}
                    {historySummary && (
                      <span className="instr-hist-chip" title="이 인스트럭션의 변경 이력 개수와 최종 수정 시각">
                        <span className="instr-hist-chip-ico">🕘</span>
                        {historySummary.count > 0 ? (
                          <>변경이력 <b>{historySummary.count}</b>개<span className="instr-hist-chip-sep">·</span>최종 {fmtTimestamp(historySummary.latestAt)}</>
                        ) : '변경이력 없음'}
                      </span>
                    )}
                  </div>
                  <span className="instr-editor-desc">
                    {INSTRUCTION_META[selectedName]?.desc || selectedName}
                  </span>
                </div>
                <span style={{ flex: 1 }} />
                {loaded?._meta?.updated_by && (
                  <span className="instr-meta">
                    by {loaded._meta.updated_by}
                  </span>
                )}
              </div>

              <div className="instr-editor-cm">
                <CodeMirror
                  value={editing}
                  onChange={setEditing}
                  height="100%"
                  theme={editorTheme || (settings?.theme === 'dark' ? oneDark : 'light')}
                  extensions={cmExtensions}
                  basicSetup={{
                    lineNumbers: true,
                    foldGutter: true,
                    highlightActiveLine: true,
                    autocompletion: false,
                  }}
                  readOnly={loadingItem}
                />
                {loadingItem && <div className="instr-loading-overlay">로드 중…</div>}
              </div>

              <div className="instr-editor-foot">
                <button
                  className="btn-secondary"
                  onClick={handleLoadDefault}
                  disabled={loadingItem}
                  title={mode === 'default'
                    ? '디스크 defaults/.md 공장값을 에디터에 채움 (저장 전까진 미반영)'
                    : '관리자가 저장한 공유 기본값을 에디터에 채움 — 없으면 디스크 공장값 (저장 전까진 미반영)'}
                >{mode === 'default' ? '공장값으로 복원' : '기본값으로 복원'}</button>
                {mode === 'user' && loaded?.has_user_override && (
                  <button
                    className="btn-secondary instr-danger"
                    onClick={handleDeleteOverride}
                    disabled={loadingItem || saving}
                    title="내 override 삭제 → 공유 기본값으로 되돌림"
                  >내 override 삭제</button>
                )}
                {/* KBNL-136: 수정 이력 — user=내 override / default=공유 기본값 */}
                <button
                  className="btn-secondary"
                  onClick={openHistory}
                  disabled={loadingItem}
                  title={mode === 'default'
                    ? '공유 기본값의 수정 이력(버전) 보기 — 과거 버전 불러오기'
                    : '내 설정의 수정 이력(버전) 보기 — 과거 버전 불러오기'}
                >이력</button>
                <span style={{ flex: 1 }} />
                <button className="btn-secondary" onClick={onClose}>닫기</button>
                <button
                  className="btn-primary"
                  onClick={handleSave}
                  disabled={!dirty || saving || loadingItem}
                >{saving ? '저장 중…' : '저장'}</button>
              </div>
            </>
          )}
        </main>
      </div>

      {/* KBNL-136: 수정 이력(버전) 패널 */}
      {historyOpen && (
        <div className="instr-history-backdrop" onClick={() => setHistoryOpen(false)}>
          <div className="instr-history-panel" onClick={e => e.stopPropagation()}>
            <div className="instr-history-head">
              <span>
                수정 이력 — {INSTRUCTION_META[selectedName]?.label || selectedName}
                <span className="instr-history-scope">
                  {mode === 'default' ? '공유 기본값' : '내 설정'}
                </span>
              </span>
              <button className="btn-xs" onClick={() => setHistoryOpen(false)}>닫기</button>
            </div>
            <div className="instr-history-body">
              {historyLoading ? (
                <div className="instr-history-empty">불러오는 중…</div>
              ) : historyEntries.length === 0 ? (
                <div className="instr-history-empty">
                  아직 저장된 이력이 없습니다. 저장할 때마다 한 버전씩 기록됩니다.
                </div>
              ) : (
                <div className="instr-history-split">
                  {/* 좌: 버전 목록 (클릭 시 우측에 전체 내용) */}
                  <ul className="instr-history-list">
                    {historyEntries.map((ent, idx) => (
                      <li
                        key={ent.id}
                        className={`instr-history-item ${selectedHistId === ent.id ? 'active' : ''}`}
                        onClick={() => selectHistoryEntry(ent.id)}
                        title="클릭하면 이 버전의 전체 내용을 오른쪽에 표시"
                      >
                        <div className="instr-history-item-main">
                          <span className="instr-history-ver">v{ent.version}</span>
                          {idx === 0 && <span className="instr-history-latest">최신</span>}
                          {typeof ent.token_count === 'number' && (
                            <span className="instr-history-tok">{ent.token_count} tok</span>
                          )}
                        </div>
                        <div className="instr-history-item-sub">
                          <span className="instr-history-time">{fmtTimestamp(ent.updated_at)}</span>
                          {ent.updated_by && <span className="instr-history-by">· {ent.updated_by}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                  {/* 우: 선택 버전의 전체 내용 (읽기 전용) */}
                  <div className="instr-history-detail">
                    <div className="instr-history-detail-head">
                      <span>
                        {selectedHistId
                          ? `v${historyEntries.find(e => e.id === selectedHistId)?.version ?? ''} 내용`
                          : '버전을 선택하세요'}
                      </span>
                      {selectedHistId && (
                        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
                          <button
                            className="btn-xs"
                            onClick={() => handleLoadHistoryEntry(selectedHistId)}
                            title="이 버전을 에디터에 불러옵니다 (저장 전까진 미반영)"
                          >이 버전 불러오기</button>
                          <button
                            className="btn-xs instr-history-del"
                            onClick={() => handleDeleteHistoryEntry(selectedHistId)}
                            title="이 버전을 이력에서 영구 삭제"
                          >🗑 삭제</button>
                        </div>
                      )}
                    </div>
                    <div className="instr-history-content">
                      {historyContentLoading ? (
                        <div className="instr-history-empty">불러오는 중…</div>
                      ) : (
                        <CodeMirror
                          value={historyContent || ''}
                          height="100%"
                          theme={editorTheme || (settings?.theme === 'dark' ? oneDark : 'light')}
                          extensions={cmExtensions}
                          basicSetup={{
                            lineNumbers: true,
                            foldGutter: true,
                            highlightActiveLine: false,
                            autocompletion: false,
                          }}
                          editable={false}
                          readOnly={true}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
